import {
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CRow,
    CButton,
    CImage
} from '@coreui/react';
import {
    Form,
    useNavigation,
    useLoaderData,
    useActionData,
    useParams,
    useNavigate
} from 'react-router-dom';
import {
    cilPencil,
    cilCalendar,
    cilAsterisk,
    cilContact,
    cilBadge,
    cilLocationPin,
    cilMoney,
    cilImage,
    cilTrash,
    cilPhone,
    cilAt
} from '@coreui/icons';
import { memo, useContext, useState } from 'react';
import Input from '../../components/Input';
import Alert from '../../components/Alert';
import { useRedirect } from '../../hooks/redirect';
import { AuthContext } from '../../store/auth';
import { ENV, ROLES } from '../../utils/constants';
import { CustomDate } from '@church/utilities';
import {
    deleteHandler,
    handleActions,
    loadResource
} from '../../utils/requests/general-request';
import CIcon from '@coreui/icons-react';
import WarningModal from '../../components/modals/WarningModal';

const eventStatus = [
    { name: 'Select Status', value: '' },
    { name: 'Upcoming', value: 'upcoming' },
    { name: 'Ongoing', value: 'ongoing' },
    { name: 'Concluded', value: 'concluded' },
    { name: 'Cancelled', value: 'cancelled' },
    { name: 'Postponed', value: 'postponed' }
];

const Event = () => {
    const [disabled, setDisabled] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [assetId, setAsssetId] = useState(0);
    const [asset, setAssset] = useState('');
    const loadedData = useLoaderData();
    const actionData = useActionData();
    const navigation = useNavigation();

    const { id } = useParams();
    const navigate = useNavigate();

    const authCtx = useContext(AuthContext);
    // On success, redirect to events!
    useRedirect(actionData, '/dashboard/events', true);

    const disableInputField = () => setDisabled(disabled => !disabled);

    const handleDeleteOptions = (asset, assetId) => {
        if (asset === 'image' && assetId !== 0) {
            setAsssetId(assetId)
            setAssset(asset)
        } else if (asset === 'event' && assetId !== 0) {
            setAsssetId(assetId)
            setAssset(asset)
        }
        setShowModal(true);
    };

    const deleteImageHandler = async () => {
        const uri =
            assetId !== 0 && asset === 'image'
                ? `${ENV.adminBaseUrl}/events/${id}/images/${assetId}/delete`
                : `${ENV.adminBaseUrl}/events/${id}`;
        const isDeleted = await deleteHandler(uri);
        if (isDeleted) {
            setShowModal(false);
            navigate('/dashboard/events');
        }
    };
    
    return (
        <>
            {showModal && (
                <WarningModal
                    title='Delete'
                    visible={showModal}
                    onClose={() => setShowModal(false)}
                    type='button'
                    onClick={deleteImageHandler}
                />
            )}
            <CRow>
                {[ROLES.admin, ROLES.super].includes(authCtx.user.roles) && (
                    <>
                        <CCol xs={12} sm={12} md={12} lg={9} xl={9}>
                            <Alert
                                data={actionData}
                                message='Event updated successfully.'
                            />
                            <CCard className='mb-4'>
                                <CCardHeader className='d-flex justify-content-between'>
                                    <small> Event Details</small>

                                    <CButton
                                        className='btn btn-success text-white'
                                        type='button'
                                        onClick={disableInputField}
                                    >
                                        Edit
                                    </CButton>
                                </CCardHeader>
                                <CCardBody>
                                    <Form
                                        method='post'
                                        noValidate
                                        encType='multipart/form-data'
                                    >
                                        <CRow>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    type='text'
                                                    id='title'
                                                    name='title'
                                                    placeholder='Title'
                                                    labelTitle='Title'
                                                    icon={cilAsterisk}
                                                    data={actionData}
                                                    defaultValue={
                                                        loadedData.data.title
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    type='text'
                                                    id='organizer'
                                                    name='organizer'
                                                    placeholder='Organizer'
                                                    labelTitle='Organizer'
                                                    data={actionData}
                                                    icon={cilContact}
                                                    defaultValue={
                                                        loadedData.data
                                                            .organizer
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                        </CRow>
                                        <CRow>
                                            <CCol xs={12}>
                                                <Input
                                                    element='textarea'
                                                    type='textarea'
                                                    id='description'
                                                    name='description'
                                                    placeholder='Max 200 letters'
                                                    labelTitle='Description'
                                                    data={actionData}
                                                    icon={cilPencil}
                                                    defaultValue={
                                                        loadedData.data
                                                            .description
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                        </CRow>
                                        <CRow>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    type='text'
                                                    id='location'
                                                    name='location'
                                                    placeholder='Location'
                                                    labelTitle='Location'
                                                    data={actionData}
                                                    icon={cilLocationPin}
                                                    defaultValue={
                                                        loadedData.data.location
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    id='images'
                                                    type='file'
                                                    name='images'
                                                    labelTitle='Images'
                                                    accept='.jpeg, .png, .jpg, .svg'
                                                    multiple
                                                    icon={cilImage}
                                                    data={actionData}
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                        </CRow>
                                        <CRow>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    type='number'
                                                    id='fee'
                                                    name='fee'
                                                    placeholder='Fee'
                                                    labelTitle='Fee (€)'
                                                    min={0}
                                                    icon={cilMoney}
                                                    data={actionData}
                                                    defaultValue={
                                                        loadedData.data.fee
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='select'
                                                    id='status'
                                                    name='status'
                                                    placeholder='Status'
                                                    labelTitle='Status'
                                                    icon={cilBadge}
                                                    data={actionData}
                                                    defaultValue={
                                                        loadedData.data.status
                                                    }
                                                    disabled={disabled}
                                                >
                                                    {eventStatus.map(status => (
                                                        <option
                                                            value={status.value}
                                                            key={status.name}
                                                        >
                                                            {status.name}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </CCol>
                                        </CRow>
                                        <CRow>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    type='email'
                                                    id='email'
                                                    name='email'
                                                    placeholder='Email'
                                                    labelTitle='Email'
                                                    icon={cilAt}
                                                    data={actionData}
                                                    defaultValue={
                                                        loadedData.data.email
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={6}
                                                xl={6}
                                            >
                                                <Input
                                                    element='input'
                                                    type='text'
                                                    id='telephone'
                                                    name='telephone'
                                                    placeholder='Telephone'
                                                    labelTitle='Telephone'
                                                    icon={cilPhone}
                                                    data={actionData}
                                                    defaultValue={
                                                        loadedData.data
                                                            .telephone
                                                    }
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                        </CRow>
                                        <CRow>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={4}
                                                xl={4}
                                            >
                                                <Input
                                                    element='input'
                                                    type='datetime-local'
                                                    id='start_date'
                                                    name='start_date'
                                                    placeholder='Start Date'
                                                    labelTitle='Start Date'
                                                    icon={cilCalendar}
                                                    data={actionData}
                                                    defaultValue={CustomDate.getDateTimeLocal(
                                                        loadedData.data
                                                            .startDate
                                                    )}
                                                    min={CustomDate.getToday()}
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={4}
                                                xl={4}
                                            >
                                                <Input
                                                    element='input'
                                                    type='datetime-local'
                                                    id='end_date'
                                                    name='end_date'
                                                    placeholder='End Date'
                                                    labelTitle='End Date'
                                                    icon={cilCalendar}
                                                    data={actionData}
                                                    defaultValue={CustomDate.getDateTimeLocal(
                                                        loadedData.data.endDate
                                                    )}
                                                    min={CustomDate.getToday()}
                                                    disabled={disabled}
                                                />
                                            </CCol>
                                            <CCol
                                                xs={12}
                                                sm={12}
                                                md={12}
                                                lg={4}
                                                xl={4}
                                            >
                                                <Input
                                                    element='select'
                                                    id='is_published'
                                                    name='is_published'
                                                    placeholder='Publish Status'
                                                    labelTitle='Publish Status'
                                                    icon={cilBadge}
                                                    data={actionData}
                                                    defaultValue={
                                                        loadedData.data
                                                            .isPublished
                                                    }
                                                    disabled={disabled}
                                                >
                                                    <option value=''>
                                                        Select publish status
                                                    </option>
                                                    <option value='yes'>
                                                        Yes
                                                    </option>
                                                    <option value='no'>
                                                        No
                                                    </option>
                                                </Input>
                                            </CCol>
                                        </CRow>
                                        {/* Hidden input to allow for update when file is involved */}
                                        <input
                                            id='_method'
                                            type='hidden'
                                            name='_method'
                                            value='PATCH'
                                        />
                                        <div className='my-4 d-flex gap-4'>
                                            <CButton
                                                className='btn-facebook'
                                                type='submit'
                                                name='intent'
                                                value='edit'
                                                disabled={
                                                    navigation.state ===
                                                        'submitting' ||
                                                    navigation.state ===
                                                        'loading' ||
                                                    disabled
                                                }
                                            >
                                                <span>
                                                    {navigation.state ===
                                                    'submitting'
                                                        ? 'Submitting...'
                                                        : 'Submit'}
                                                </span>
                                            </CButton>

                                            {navigation.state !==
                                                'submitting' && (
                                                <CButton
                                                    className='btn btn-danger text-white'
                                                    type='button'
                                                    onClick={() => 
                                                    {
                                                        handleDeleteOptions('event', id)
                                                        setShowModal(true)
                                                        
                                                    }
                                                    
                                                        
                                                    }
                                                >
                                                    <span>Delete</span>
                                                </CButton>
                                            )}
                                        </div>
                                    </Form>
                                </CCardBody>
                            </CCard>
                        </CCol>
                        {loadedData && loadedData.data.images.length > 0 && (
                            <CCol xs={12} sm={12} md={12} lg={3} xl={3}>
                                <CCard className='image-wrapper'>
                                    <CCardBody>
                                        <CImage
                                            src={`${ENV.images}/${loadedData.data.images[0].url}`}
                                            height='100%'
                                            width='100%'
                                            alt={loadedData.data.title}
                                        />
                                    </CCardBody>
                                    <CButton
                                        className='btn btn-danger image-button text-white'
                                        type='button'
                                        onClick={() =>
                                            handleDeleteOptions(
                                                'image',
                                                loadedData.data.images[0].id
                                            )
                                        }
                                    >
                                        <CIcon icon={cilTrash} />
                                    </CButton>
                                </CCard>
                            </CCol>
                        )}
                    </>
                )}
            </CRow>
        </>
    );
};

export const action = async ({ request, params }) => {
    return await handleActions(request, {
        uri: `${ENV.adminBaseUrl}/events/${params.id}`,
        isFormData: true,
        assets: 'images'
    });
};

export const loader = async ({ request, params }) => {
    return await loadResource(request, {
        uri: `${ENV.adminBaseUrl}/events/${params.id}`
    });
};

export default memo(Event);
